import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { 
  EnvelopeIcon, 
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';

const EmailConfiguration = () => {
  const [categories, setCategories] = useState([]);
  const [categoryTemplates, setCategoryTemplates] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notificationEmails, setNotificationEmails] = useState('');
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [recipientsError, setRecipientsError] = useState(null);
  const [templateData, setTemplateData] = useState({
    subject: '',
    htmlContent: '',
    textContent: '',
    isActive: true
  });

  const templateVariables = [
    { name: '{UserName}', description: 'Name of the ticket creator' },
    { name: '{TicketId}', description: 'Ticket ID number' },
    { name: '{Title}', description: 'Ticket title' },
    { name: '{Description}', description: 'Ticket description' },
    { name: '{Priority}', description: 'Ticket priority level' },
    { name: '{Status}', description: 'Current ticket status' },
    { name: '{CreatedAt}', description: 'Ticket creation date/time' },
    { name: '{CategoryName}', description: 'Category name' },
    { name: '{TicketUrl}', description: 'Direct link to the ticket' }
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, templatesData] = await Promise.all([
        apiService.getCategories(),
        apiService.getCategoryEmailTemplates()
      ]);
      
      setCategories(categoriesData);
      
      // Convert templates array to object for easier lookup
      const templatesObj = {};
      templatesData.forEach(template => {
        templatesObj[template.categoryName] = template;
      });
      setCategoryTemplates(templatesObj);
      
      if (categoriesData.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesData[0].name);
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = async (categoryName) => {
    setSelectedCategory(categoryName);
    setIsEditing(false);
    setRecipientsError(null);
    
    // Load template data for selected category
    if (categoryTemplates[categoryName]) {
      const template = categoryTemplates[categoryName];
      setTemplateData({
        subject: template.subject,
        htmlContent: template.htmlContent,
        textContent: template.textContent,
        isActive: template.isActive
      });
    } else {
      // Set default template for new category
      setTemplateData({
        subject: `New ${categoryName} Ticket Created - #{TicketId} {Title}`,
        htmlContent: getDefaultHtmlTemplate(categoryName),
        textContent: getDefaultTextTemplate(categoryName),
        isActive: true
      });
    }

    // Load category-specific notification email recipients from SystemSettings
    try {
      const emails = await apiService.getCategoryNotificationEmails(categoryName);
      setNotificationEmails(emails || '');
    } catch (err) {
      console.error('Failed to load category notification emails:', err);
      setNotificationEmails('');
      setRecipientsError('Failed to load technician notification emails');
    }
  };

  const getDefaultHtmlTemplate = (categoryName) => {
    return `<html>
<body style='font-family: Arial, sans-serif; line-height: 1.6; color: #333;'>
    <div style='max-width: 600px; margin: 0 auto; padding: 20px;'>
        <h2 style='color: #2563eb;'>New ${categoryName} Ticket Created</h2>
        <p>Dear {UserName},</p>
        <p>A new ${categoryName} ticket has been created:</p>
        
        <div style='background-color: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;'>
            <h3 style='margin-top: 0;'>Ticket #{TicketId}</h3>
            <p><strong>Title:</strong> {Title}</p>
            <p><strong>Description:</strong> {Description}</p>
            <p><strong>Priority:</strong> {Priority}</p>
            <p><strong>Status:</strong> {Status}</p>
            <p><strong>Created:</strong> {CreatedAt}</p>
        </div>
        
        <p>You can track the progress of your ticket by visiting:</p>
        <p><a href='{TicketUrl}' style='color: #2563eb;'>{TicketUrl}</a></p>
        
        <p>Best regards,<br>Alula IT Helpdesk Team</p>
    </div>
</body>
</html>`;
  };

  const getDefaultTextTemplate = (categoryName) => {
    return `New ${categoryName} Ticket Created

Dear {UserName},

A new ${categoryName} ticket has been created:

Ticket #{TicketId}
Title: {Title}
Description: {Description}
Priority: {Priority}
Status: {Status}
Created: {CreatedAt}

You can track the progress of your ticket by visiting:
{TicketUrl}

Best regards,
Alula IT Helpdesk Team`;
  };

  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const templateDto = {
        categoryName: selectedCategory,
        subject: templateData.subject,
        htmlContent: templateData.htmlContent,
        textContent: templateData.textContent,
        isActive: templateData.isActive
      };
      
      const savedTemplate = await apiService.createOrUpdateCategoryEmailTemplate(templateDto);
      
      // Update local state
      setCategoryTemplates(prev => ({
        ...prev,
        [selectedCategory]: savedTemplate
      }));
      
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRecipients = async () => {
    if (!selectedCategory) return;

    try {
      setSavingRecipients(true);
      setRecipientsError(null);

      const trimmed = notificationEmails.trim();

      // Allow empty value to clear notifications for the category
      if (trimmed.length === 0) {
        await apiService.setCategoryNotificationEmails(selectedCategory, '');
        return;
      }

      const emails = trimmed
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      const invalid = emails.filter(
        (e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)
      );

      if (invalid.length > 0) {
        setRecipientsError(`Invalid email(s): ${invalid.join(', ')}`);
        return;
      }

      await apiService.setCategoryNotificationEmails(
        selectedCategory,
        emails.join(',')
      );
    } catch (err) {
      console.error('Failed to save category notification emails:', err);
      setRecipientsError('Failed to save technician notification emails');
    } finally {
      setSavingRecipients(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!window.confirm(`Are you sure you want to delete the template for "${selectedCategory}"?`)) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      await apiService.deleteCategoryEmailTemplate(selectedCategory);
      
      // Remove from local state
      const newTemplates = { ...categoryTemplates };
      delete newTemplates[selectedCategory];
      setCategoryTemplates(newTemplates);
      
      // Reset template data
      setTemplateData({
        subject: `New ${selectedCategory} Ticket Created - #{TicketId} {Title}`,
        htmlContent: getDefaultHtmlTemplate(selectedCategory),
        textContent: getDefaultTextTemplate(selectedCategory),
        isActive: true
      });
      
      setIsEditing(false);
    } catch (err) {
      setError('Failed to delete template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Email Configuration
        </h2>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        <Tab.Group>
          <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1">
            <Tab className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
               ${selected ? 'bg-white text-blue-700 shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
            }>
              <div className="flex items-center justify-center space-x-2">
                <EnvelopeIcon className="h-5 w-5" />
                <span>General Templates</span>
              </div>
            </Tab>
            <Tab className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
               ${selected ? 'bg-white text-blue-700 shadow' : 'text-blue-100 hover:bg-white/[0.12] hover:text-white'}`
            }>
              <div className="flex items-center justify-center space-x-2">
                <DocumentTextIcon className="h-5 w-5" />
                <span>Category Templates</span>
              </div>
            </Tab>
          </Tab.List>
          
          <Tab.Panels className="mt-6">
            <Tab.Panel>
              {/* Existing general email template content */}
              <div className="text-center py-8">
                <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">General Email Templates</h3>
                <p className="mt-1 text-sm text-gray-500">
                  General email template management will be implemented here.
                </p>
              </div>
            </Tab.Panel>
            
            <Tab.Panel>
              <CategoryTemplateManager 
                categories={categories}
                categoryTemplates={categoryTemplates}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                templateData={templateData}
                setTemplateData={setTemplateData}
                templateVariables={templateVariables}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                saving={saving}
                onSave={handleSaveTemplate}
                onDelete={handleDeleteTemplate}
                onCategorySelect={handleCategorySelect}
                notificationEmails={notificationEmails}
                setNotificationEmails={setNotificationEmails}
                savingRecipients={savingRecipients}
                recipientsError={recipientsError}
                onSaveRecipients={handleSaveRecipients}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

const CategoryTemplateManager = ({ 
  categories, 
  categoryTemplates, 
  selectedCategory, 
  templateData,
  setTemplateData,
  templateVariables,
  isEditing,
  setIsEditing,
  saving,
  onSave,
  onDelete,
  onCategorySelect,
  notificationEmails,
  setNotificationEmails,
  savingRecipients,
  recipientsError,
  onSaveRecipients
}) => {
  return (
    <div className="space-y-6">
      {/* Category Selection */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Category-Specific Email Templates
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => onCategorySelect(category.name)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                selectedCategory === category.name
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <h4 className="font-medium text-gray-900">{category.name}</h4>
              <p className="text-sm text-gray-600 mt-1">
                {categoryTemplates[category.name] ? 'Template configured' : 'No template'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Template Editor */}
      {selectedCategory && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Template for: {selectedCategory}
            </h3>
            <div className="flex space-x-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit Template
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={onSave}
                    disabled={saving}
                    className="flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Cancel
                  </button>
                  {categoryTemplates[selectedCategory] && (
                    <button
                      onClick={onDelete}
                      disabled={saving}
                      className="flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {isEditing ? (
            <TemplateEditor 
              templateData={templateData}
              setTemplateData={setTemplateData}
              templateVariables={templateVariables}
              categoryName={selectedCategory}
            />
          ) : (
            <TemplateReadOnly templateData={templateData} />
          )}

          {/* Technician Notification Recipients */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-2">
              Technician Notification Recipients
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Comma-separated list of email addresses that should receive
              category-specific notifications when a new ticket is created in{' '}
              <strong>{selectedCategory}</strong>. Leave empty to disable
              category notifications for this category.
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email addresses
            </label>
            <input
              type="text"
              value={notificationEmails}
              onChange={(e) => setNotificationEmails(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="tech1@company.com, team@company.com"
            />

            {recipientsError && (
              <p className="mt-2 text-sm text-red-600">{recipientsError}</p>
            )}

            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={onSaveRecipients}
                disabled={savingRecipients}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingRecipients ? 'Saving...' : 'Save Technician Emails'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TemplateEditor = ({ templateData, setTemplateData, templateVariables, categoryName }) => {
  return (
    <div className="space-y-4">
      {/* Subject Line */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Subject
        </label>
        <input
          type="text"
          value={templateData.subject}
          onChange={(e) => setTemplateData({...templateData, subject: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="New {CategoryName} Ticket Created - #{TicketId} {Title}"
        />
      </div>

      {/* Template Variables */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Available Variables</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {templateVariables.map((variable) => (
            <div key={variable.name} className="flex items-center space-x-2">
              <code className="text-xs bg-white px-2 py-1 rounded border">
                {variable.name}
              </code>
              <span className="text-xs text-gray-600">{variable.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* HTML Content */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          HTML Content
        </label>
        <textarea
          value={templateData.htmlContent}
          onChange={(e) => setTemplateData({...templateData, htmlContent: e.target.value})}
          rows={15}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          placeholder="<html>...</html>"
        />
      </div>

      {/* Text Content */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Text Content (Fallback)
        </label>
        <textarea
          value={templateData.textContent}
          onChange={(e) => setTemplateData({...templateData, textContent: e.target.value})}
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Plain text version..."
        />
      </div>
    </div>
  );
};

const TemplateReadOnly = ({ templateData }) => (
  <div className="space-y-4">
    <p className="text-sm text-gray-600">
      To see how this looks in email, send a test message where SendGrid is enabled (Staging redirects to the test inbox) rather than previewing HTML here.
    </p>
    <div>
      <strong>Subject:</strong> {templateData.subject}
    </div>
    <div>
      <strong>HTML content:</strong>
      <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-96">
        {templateData.htmlContent}
      </pre>
    </div>
    <div>
      <strong>Text content:</strong>
      <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-48 whitespace-pre-wrap">
        {templateData.textContent}
      </pre>
    </div>
  </div>
);

export default EmailConfiguration;
